import _axios, { AxiosError, AxiosResponse, InternalAxiosRequestConfig, isAxiosError } from 'axios';
import { jwtDecode } from 'jwt-decode';
import { clearAuthTokens, getAuthTokens, getTenantId, setAuthTokens } from './utils';
import i18n from "i18next";
import { useCallback, useState } from 'react';

const baseURL = process.env.REACT_APP_API_ADDRESS;
// Set on a request once it has been retried with a refreshed token, so a
// request the server still refuses goes back to the login instead of looping.
type RetriedRequestConfig = InternalAxiosRequestConfig & { _retriedAfterRefresh?: boolean };
const timeout = 8000; // 8 sec
// Main axios instance
const axios = _axios.create({ baseURL, timeout });
let reloadingForGate = false;
const isTokenExpired = (token: string): boolean => {
  try {
    const { exp } = jwtDecode<{ exp: number }>(token);
    return Date.now() >= exp * 1000;
  } catch (e) {
    return true;
  }
};

axios.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
  const authTokens = getAuthTokens();
  const tenantId = getTenantId();

  if (!config.headers['Accept-Language']) {
    config.headers['Accept-Language'] = i18n.language;
  }

  if (!config.headers.Tenant && tenantId) {
    config.headers.Tenant = tenantId;
  }

  if (!authTokens?.access || !authTokens?.refresh) return config;
  if (!config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${authTokens.access}`;
  }

  return config;
});

axios.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as RetriedRequestConfig | undefined;

    if (error.response?.status === 401 && originalRequest && originalRequest._retriedAfterRefresh) {
      clearAuthTokens();
      return Promise.reject(error);
    }

    if (error.response?.status === 401 && originalRequest) {

      try {
        const authTokens = getAuthTokens();
        const refresh = authTokens?.refresh;

        // ⛔ Don't try refresh if it's already expired
        if (!refresh || isTokenExpired(refresh)) {
          clearAuthTokens();
          return Promise.reject(error);
        }

        const tenantId = getTenantId();
        const refreshAxios = _axios.create({
          baseURL,
          timeout,
          headers: {
            Tenant: tenantId,
          },
        });
        const response = await refreshAxios.post('/auth/token/refresh/', {
          refresh,
        });

        const { access } = response.data;
        setAuthTokens({ access }); // Update access token only
        originalRequest.headers.Authorization = `Bearer ${access}`;
        originalRequest._retriedAfterRefresh = true;
        return axios(originalRequest);
      } catch (refreshError) {
        clearAuthTokens();
        return Promise.reject(refreshError);
      }
    }

    // The workspace's subscription blocks access (it lapsed while the app was
    // open): reload, so the app comes back up behind the subscription gate.
    const errorData = error.response?.data as { code?: string } | undefined;
    if (error.response?.status === 402 && errorData?.code === 'subscription_blocked' && !reloadingForGate) {
      reloadingForGate = true;
      window.location.reload();
    }

    return Promise.reject(error);
  }
);

export const useApiRequest = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<any>();

  const executeRequest = useCallback(async (request: () => Promise<any>) => {
    try {
      setError(undefined);
      setLoading(true);
      const response = await request();
      return { data: response.data, success: true };
    } catch (errorResponse) {
      const errorData = isAxiosError(errorResponse)
        ? errorResponse.response?.data
        : "An unexpected error occurred";
      setError(errorData);
      return { data: null, success: false, errorData };
    } finally {
      setLoading(false);
    }
  }, []);

  return { loading, error, executeRequest };
};

export default axios;
